import { Injectable } from '@angular/core';
import { BiometricAuth, BiometryType, BiometryErrorType } from '@aparajita/capacitor-biometric-auth';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { Capacitor } from '@capacitor/core';
import {TranslationService} from "./translation.service";

@Injectable({
  providedIn: 'root'
})
export class BiometricService {

  private readonly BIOMETRIC_CREDENTIALS_KEY = 'biometric_credentials';
  private readonly BIOMETRIC_ENABLED_KEY = 'biometric_enabled';
  private readonly BIOMETRIC_TMP_CREDENTIALS_KEY = 'biometric_tmp_credentials';
  public readonly BIOMETRIC_DONT_ASK_AGAIN = 'biometric_dont_ask_again';

  constructor(
      private _translationService: TranslationService,
  ) {

  }

  /**
   * Check if biometric authentication is available on the device
   */
  async isBiometricAvailable(): Promise<boolean> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return false;
      }

      const result = await BiometricAuth.checkBiometry();
      return result.isAvailable;
    } catch (error) {
      console.error('Error checking biometric availability:', error);
      return false;
    }
  }

  /**
   * Get the available biometry types (Face ID, Touch ID, Fingerprint, etc.)
   */
  async getBiometryType(): Promise<BiometryType | null> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return null;
      }

      const result = await BiometricAuth.checkBiometry();
      return result.biometryType;
    } catch (error) {
      console.error('Error getting biometry type:', error);
      return null;
    }
  }

  /**
   * Get the display name for the biometric type
   */
  async getBiometricDisplayName(): Promise<string> {
    const biometryType = await this.getBiometryType();
    
    switch (biometryType) {
      case BiometryType.faceId:
        return 'Face ID';
      case BiometryType.touchId:
        return 'Touch ID';
      case BiometryType.fingerprintAuthentication:
        return 'Fingerprint';
      case BiometryType.faceAuthentication:
        return 'Face Authentication';
      case BiometryType.irisAuthentication:
        return 'Iris Authentication';
      default:
        return 'Biometric';
    }
  }

  /**
   * Get the appropriate icon name for the biometric type
   */
  async getBiometricIcon(): Promise<string> {
    const biometryType = await this.getBiometryType();
    
    switch (biometryType) {
      case BiometryType.faceId:
      case BiometryType.faceAuthentication:
        return 'scan-outline';
      case BiometryType.touchId:
      case BiometryType.fingerprintAuthentication:
        return 'finger-print-outline';
      case BiometryType.irisAuthentication:
        return 'eye-outline';
      default:
        return 'finger-print-outline';
    }
  }

  /**
   * Authenticate using biometrics
   */
  async authenticateWithBiometrics(): Promise<{ success: boolean; error?: string }> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return { success: false, error: this._translationService.translate('Biometric authentication not available on web platform') };
      }

      const biometricName = await this.getBiometricDisplayName();

      const result = await BiometricAuth.authenticate({
        reason: this._translationService.translate(`Use ${biometricName} to sign in to your account`),
        cancelTitle: this._translationService.translate('Cancel'),
      });

      return { success: true };
    } catch (error: any) {
      console.error('Biometric authentication error:', error);
      
      let errorMessage = 'Biometric authentication failed';
      
      if (error.code) {
        switch (error.code) {
          case BiometryErrorType.biometryNotAvailable:
            errorMessage = 'Biometric authentication is not available';
            break;
          case BiometryErrorType.biometryNotEnrolled:
            errorMessage = 'No biometric credentials are enrolled on this device';
            break;
          case BiometryErrorType.userCancel:
            errorMessage = 'Authentication was cancelled';
            break;
          case BiometryErrorType.userFallback:
            errorMessage = 'User chose to use fallback authentication';
            break;
          case BiometryErrorType.biometryLockout:
            errorMessage = 'Biometric authentication is locked out';
            break;
          case BiometryErrorType.authenticationFailed:
            errorMessage = 'Biometric authentication failed';
            break;
          default:
            errorMessage = error.message || 'Biometric authentication failed';
        }
      }

      return { success: false, error: errorMessage };
    }
  }

  /**
   * Check if biometric login is enabled by the user
   */
  async isBiometricEnabled(): Promise<boolean> {
    const enabled = await SecureStorage.getItem(this.BIOMETRIC_ENABLED_KEY);
    return enabled === 'true';
  }

  /**
   * Enable or disable biometric login
   */
  async setBiometricEnabled(enabled: boolean): Promise<void> {
    try {
      await SecureStorage.setItem(this.BIOMETRIC_ENABLED_KEY, enabled.toString());

      // If disabling, also remove stored credentials
      if (!enabled) {
        await this.removeBiometricCredentials();
      }

    } catch (error) {
      console.error('Error removing credentials when disabling biometric:', error);
    }
  }

  /**
   * Store biometric credentials securely in device keychain
   */
  async storeBiometricCredentials(username: string, password: string): Promise<void> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return;
      }

      const credentials = {
        username,
        password
      };
      
      await SecureStorage.set(this.BIOMETRIC_CREDENTIALS_KEY, JSON.stringify(credentials));
    } catch (error) {
      console.error('Error storing biometric credentials:', error);
      throw error;
    }
  }

  async storeTemporaryCredentials(username: string, password: string): Promise<void> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return;
      }

      const credentials = {
        username,
        password
      };
      
      await SecureStorage.set(this.BIOMETRIC_TMP_CREDENTIALS_KEY, JSON.stringify(credentials));
    } catch (error) {
      console.error('Error storing biometric credentials:', error);
      throw error;
    }
  }

  async getTemporaryCredentials(): Promise<any | null> {
    try {
      let stored: string | null = null;
      
      if (!Capacitor.isNativePlatform()) {
        return null;
      } else {
        const result = await SecureStorage.get(this.BIOMETRIC_TMP_CREDENTIALS_KEY);
        stored = result ? String(result) : null;
      }
      
      if (!stored) {
        return null;
      }

      return JSON.parse(stored);
    } catch (error) {
      console.error('Error retrieving biometric credentials:', error);
      return null;
    }
  }

    /**
   * Remove stored biometric credentials from secure storage
   */
  async removeTemporaryCredentials(): Promise<void> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return;
      }

      await SecureStorage.remove(this.BIOMETRIC_TMP_CREDENTIALS_KEY);
    } catch (error) {
      console.error('Error removing biometric credentials:', error);
      // Don't throw error here as this is cleanup
    }
  }

  /**
   * Retrieve stored biometric credentials from secure storage
   */
  async getBiometricCredentials(): Promise<any | null> {
    try {
      let stored: string | null = null;
      
      if (!Capacitor.isNativePlatform()) {
        return null;
      } else {
        const result = await SecureStorage.get(this.BIOMETRIC_CREDENTIALS_KEY);
        stored = result ? String(result) : null;
      }
      
      if (!stored) {
        return null;
      }

      return JSON.parse(stored);
    } catch (error) {
      console.error('Error retrieving biometric credentials:', error);
      return null;
    }
  }

  /**
   * Remove stored biometric credentials from secure storage
   */
  async removeBiometricCredentials(): Promise<void> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return;
      }

      await SecureStorage.remove(this.BIOMETRIC_CREDENTIALS_KEY);
    } catch (error) {
      console.error('Error removing biometric credentials:', error);
      // Don't throw error here as this is cleanup
    }
  }

  /**
   * Check if biometric credentials are stored and valid
   */
  async hasBiometricCredentials(): Promise<boolean> {
    const credentials = await this.getBiometricCredentials();
    return credentials !== null;
  }

  /**
   * Perform complete biometric login flow
   */
  async performBiometricLogin(): Promise<{ success: boolean; credentials?: any; error?: string }> {
    try {
      // Check if biometric is available and enabled
      const isAvailable = await this.isBiometricAvailable();
      if (!isAvailable) {
        return { success: false, error: 'Biometric authentication not available' };
      }

      if (!this.isBiometricEnabled()) {
        return { success: false, error: 'Biometric login is not enabled' };
      }

      // Check if we have stored credentials
      const credentials = await this.getBiometricCredentials();
      if (!credentials) {
        return { success: false, error: 'No biometric credentials found' };
      }

      // Perform biometric authentication
      const authResult = await this.authenticateWithBiometrics();
      if (!authResult.success) {
        return { success: false, error: authResult.error };
      }

      // Return the stored credentials for login
      return { success: true, credentials };
    } catch (error: any) {
      console.error('Error in biometric login flow:', error);
      return { success: false, error: error.message || 'Biometric login failed' };
    }
  }
}
